import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Dialog, DialogContent, DialogActions, Typography, Button, IconButton,
  ToggleButton, ToggleButtonGroup, CircularProgress, Alert, Tooltip,
} from '@mui/material';
import { Close, Print, GridOn, ReceiptLong } from '@mui/icons-material';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { churchSettingsURL, paymentVouchersByChequeURL } from '../BaseURL';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('church_token')}` });

const NAVY = '#0B2545';
const GOLD = '#C89211';

const fmt = (v) => Number(v || 0).toLocaleString('en-GH', {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt.getTime())) return String(d);
  return dt.toLocaleDateString('en-GH', { day: '2-digit', month: 'short', year: 'numeric' });
};

// ─── Amount in words ────────────────────────────────────────────────────────
const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
  'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
  'Seventeen', 'Eighteen', 'Nineteen'];
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
const SCALES = [[1e9, 'Billion'], [1e6, 'Million'], [1e3, 'Thousand']];

function underHundred(n) {
  if (n < 20) return ONES[n];
  const t = Math.floor(n / 10), o = n % 10;
  return TENS[t] + (o ? `-${ONES[o]}` : '');
}

function underThousand(n) {
  if (n < 100) return underHundred(n);
  const h = Math.floor(n / 100), r = n % 100;
  return `${ONES[h]} Hundred${r ? ` and ${underHundred(r)}` : ''}`;
}

function inWords(n) {
  n = Math.floor(Math.abs(n));
  if (n === 0) return 'Zero';
  const parts = [];
  for (const [value, name] of SCALES) {
    if (n >= value) {
      parts.push(`${underThousand(Math.floor(n / value))} ${name}`);
      n %= value;
    }
  }
  if (n) parts.push(underThousand(n));
  return parts.join(' ');
}

// "1,204.50" → "One Thousand Two Hundred and Four Ghana Cedis and Fifty Pesewas Only"
function amountInWords(amount) {
  const total = Math.round(Number(amount || 0) * 100);
  const cedis = Math.floor(total / 100);
  const pesewas = total % 100;
  const head = `${inWords(cedis)} Ghana Cedi${cedis === 1 ? '' : 's'}`;
  const tail = pesewas ? ` and ${inWords(pesewas)} Pesewa${pesewas === 1 ? '' : 's'}` : '';
  return `${head}${tail} Only`;
}

/**
 * Payment voucher — printable on A5 and exportable to Excel.
 *
 * A voucher covers either the single clicked payment or every payment drawn on
 * the same cheque (the batch case), chosen with the toggle in the footer.
 */
export default function PaymentVoucherDialog({ open, onClose, payment: incoming, initialScope }) {
  // Hold on to the last payment so the dialog can play its close animation
  // instead of vanishing the moment the parent clears its selection.
  const keptRef = useRef(null);
  if (incoming) keptRef.current = incoming;
  const payment = incoming || keptRef.current;

  const [church, setChurch]     = useState(null);
  const [siblings, setSiblings] = useState([]);   // all vouchers on this cheque
  const [scope, setScope]       = useState('one'); // 'one' | 'cheque'
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const sheetRef = useRef(null);

  const chequeNo = String(payment?.cheque_no || '').trim();

  useEffect(() => {
    if (!open || !payment) return;
    setError('');
    // A caller that already knows it wants the whole cheque says so; otherwise
    // the voucher covers just the row that was clicked and the toggle below
    // offers the combined version.
    setScope(initialScope === 'cheque' ? 'cheque' : 'one');
    setSiblings([]);

    axios.get(churchSettingsURL, { headers: headers() })
      .then(r => setChurch(r.data.data || null))
      .catch(() => {});

    if (!chequeNo) return;
    setLoading(true);
    axios.get(paymentVouchersByChequeURL, { headers: headers(), params: { cheque_no: chequeNo } })
      .then(r => setSiblings(r.data.data || []))
      .catch(e => setError(e.response?.data?.message || 'Could not load the other payments on this cheque.'))
      .finally(() => setLoading(false));
  }, [open, payment, chequeNo, initialScope]);

  const isCheque = scope === 'cheque' && siblings.length > 1;
  const lines = isCheque ? siblings : (payment ? [payment] : []);
  const total = lines.reduce((s, p) => s + Number(p.amount || 0), 0);

  const first = lines[0] || {};
  const payees = [...new Set(lines.map(p => (p.request_holder || '').trim()).filter(Boolean))];
  const payeeLabel = payees.length === 0 ? '—'
    : payees.length === 1 ? payees[0]
    : `${payees[0]} and ${payees.length - 1} other${payees.length === 2 ? '' : 's'}`;

  const voucherRef = isCheque
    ? `CHQ ${chequeNo}`
    : (payment?.pv_number || '—');

  const title = isCheque
    ? `Payment Voucher — Cheque ${chequeNo}`
    : `Payment Voucher — ${payment?.pv_number || ''}`;

  // ─── Print ────────────────────────────────────────────────────────────────
  const handlePrint = useCallback(() => {
    if (!sheetRef.current) return;
    const w = window.open('', '_blank', 'width=860,height=900');
    if (!w) {
      setError('Your browser blocked the print window. Allow pop-ups for this site and try again.');
      return;
    }
    w.document.write(`
      <html><head><title>${title}</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif; color: #111; }
        ${SHEET_CSS}
        /* One voucher per A5 sheet; long ones simply flow onto a second page. */
        @page { size: A5 portrait; margin: 8mm; }
        .sheet { width: auto; min-height: 0; padding: 0; box-shadow: none; margin: 0; }
        thead { display: table-header-group; }
        tr { page-break-inside: avoid; }
        .sign-block { page-break-inside: avoid; }
      </style>
      </head><body>${sheetRef.current.innerHTML}</body></html>
    `);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 300);
  }, [title]);

  // ─── Excel ────────────────────────────────────────────────────────────────
  const handleExcel = () => {
    const contact = [church?.address, church?.phone, church?.email].filter(Boolean).join('  ·  ');
    const aoa = [
      [church?.name || 'Church'],
      [contact],
      [],
      ['PAYMENT VOUCHER'],
      [],
      ['Voucher Ref', voucherRef, '', 'Date', isCheque ? `${fmtDate(first.payment_date)} — ${fmtDate(lines[lines.length - 1]?.payment_date)}` : fmtDate(first.payment_date)],
      ['Cheque No', chequeNo || '—', '', 'Payment Mode', (first.payment_mode || 'bank').toUpperCase()],
      ['Paid From', first.account_name || '—', '', 'Payee', payeeLabel],
      [],
      ['#', 'Date', 'PV No', 'Expense Type', 'Category', 'Request Holder', 'Description', 'Amount (GH₵)'],
      ...lines.map((p, i) => [
        i + 1,
        fmtDate(p.payment_date),
        p.pv_number || '—',
        p.expense_type || (p.is_bank_to_bank ? 'Bank Transfer' : '—'),
        p.is_bank_to_bank ? 'BANK-TO-BANK' : (p.expense_category || '—'),
        p.request_holder || '—',
        p.description || '—',
        Number(p.amount || 0),
      ]),
      ['', '', '', '', '', '', 'TOTAL', Number(total)],
      [],
      ['Amount in words', amountInWords(total)],
      [],
      ['Prepared by', '', '', 'Authorised by', '', '', 'Received by'],
      ['Name:', '', '', 'Name:', '', '', 'Name:'],
      ['Signature:', '', '', 'Signature:', '', '', 'Signature:'],
      ['Date:', '', '', 'Date:', '', '', 'Date:'],
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws['!cols'] = [
      { wch: 5 }, { wch: 14 }, { wch: 18 }, { wch: 24 },
      { wch: 22 }, { wch: 20 }, { wch: 36 }, { wch: 16 },
    ];
    // Right-align and 2dp the amount column.
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let R = range.s.r; R <= range.e.r; R++) {
      const addr = XLSX.utils.encode_cell({ r: R, c: 7 });
      if (ws[addr] && ws[addr].t === 'n') {
        ws[addr].z = '#,##0.00';
        ws[addr].s = { alignment: { horizontal: 'right' } };
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Payment Voucher');
    const stamp = (isCheque ? `Cheque_${chequeNo}` : (payment?.pv_number || 'Voucher')).replace(/[/\\\s]/g, '_');
    XLSX.writeFile(wb, `Payment_Voucher_${stamp}.xlsx`);
  };

  if (!payment) return null;

  const contact = [church?.address, church?.phone, church?.email].filter(Boolean).join('  ·  ');
  const dateLabel = isCheque && lines.length > 1
    ? `${fmtDate(first.payment_date)} — ${fmtDate(lines[lines.length - 1].payment_date)}`
    : fmtDate(first.payment_date);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 2, height: { xs: '100%', sm: '92vh' } } }}>
      {/* Toolbar */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2,
        background: `linear-gradient(90deg, ${NAVY}, #13315C)`, color: '#fff', px: 3, py: 2,
        flexShrink: 0,
      }}>
        <Box sx={{
          width: 38, height: 38, borderRadius: 1.5, bgcolor: 'secondary.main',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ReceiptLong />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Payment Voucher</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {voucherRef} · {lines.length} item{lines.length === 1 ? '' : 's'} · GH₵ {fmt(total)}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
      </Box>

      {/* Scope switch — only meaningful when the cheque paid more than one expense */}
      {(loading || siblings.length > 1) && (
        <Box sx={{
          px: 3, py: 1.5, bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0',
          display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', flexShrink: 0,
        }}>
          {loading ? (
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                Checking for other payments on cheque {chequeNo}…
              </Typography>
            </Box>
          ) : (
            <>
              <Typography variant="caption" fontWeight={700} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10 }}>
                Raise voucher for
              </Typography>
              <ToggleButtonGroup
                size="small" exclusive value={scope}
                onChange={(e, v) => v && setScope(v)}
                sx={{ bgcolor: '#fff' }}
              >
                <ToggleButton value="one" sx={{ textTransform: 'none', px: 2 }}>
                  This payment only
                </ToggleButton>
                <ToggleButton value="cheque" sx={{ textTransform: 'none', px: 2 }}>
                  Whole cheque · {siblings.length} payments
                </ToggleButton>
              </ToggleButtonGroup>
            </>
          )}
        </Box>
      )}

      <DialogContent sx={{ p: 0, bgcolor: '#E9EDF2', overflowY: 'auto' }}>
        {error && <Alert severity="warning" sx={{ m: 2, borderRadius: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {/* A5 sheet — this exact markup is what gets printed */}
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3, px: 2 }}>
          <Box ref={sheetRef} sx={{ '& *': { boxSizing: 'border-box' } }}>
            <style>{SHEET_CSS}</style>
            <div className="sheet">
              {/* Letterhead */}
              <div className="letterhead">
                {church?.logo ? <img src={church.logo} alt="" /> : <div className="logo-fallback">⛪</div>}
                <div className="info">
                  <h1>{church?.name || 'Church'}</h1>
                  {contact && <p>{contact}</p>}
                </div>
              </div>

              <div className="doc-title"><span>PAYMENT VOUCHER</span></div>

              {/* Meta grid */}
              <table className="meta">
                <tbody>
                  <tr>
                    <th>Voucher Ref</th><td className="mono">{voucherRef}</td>
                    <th>Date</th><td>{dateLabel}</td>
                  </tr>
                  <tr>
                    <th>Cheque No</th><td className="mono">{chequeNo || '—'}</td>
                    <th>Payment Mode</th><td>{(first.payment_mode || 'bank').toUpperCase()}</td>
                  </tr>
                  <tr>
                    <th>Paid From</th><td>{first.account_name || '—'}</td>
                    <th>Payee</th><td>{payeeLabel}</td>
                  </tr>
                </tbody>
              </table>

              {/* Particulars */}
              <table className="items">
                <thead>
                  <tr>
                    <th className="num">#</th>
                    <th>Particulars</th>
                    <th className="right">Amount (GH₵)</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((p, i) => (
                    <tr key={p.id ?? i}>
                      <td className="num">{i + 1}</td>
                      <td>
                        <div className="item-main">
                          {p.expense_type || (p.is_bank_to_bank ? 'Bank Transfer' : '—')}
                        </div>
                        <div className="item-sub">
                          {p.is_bank_to_bank ? 'BANK-TO-BANK' : (p.expense_category || '—')}
                          {isCheque && p.payment_date ? ` · ${fmtDate(p.payment_date)}` : ''}
                          {isCheque && p.request_holder ? ` · ${p.request_holder}` : ''}
                        </div>
                        {p.description && <div className="item-desc">{p.description}</div>}
                        {isCheque && p.pv_number && <div className="item-pv">{p.pv_number}</div>}
                      </td>
                      <td className="right mono">{fmt(p.amount)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="total-row">
                    <td colSpan={2}>TOTAL</td>
                    <td className="right mono">{fmt(total)}</td>
                  </tr>
                </tfoot>
              </table>

              <div className="words">
                <span className="words-label">Amount in words</span>
                <span className="words-value">{amountInWords(total)}</span>
              </div>

              {/* Signatures */}
              <div className="sign-block">
                {['Prepared by', 'Authorised by', 'Received by'].map(role => (
                  <div className="sign" key={role}>
                    <div className="sign-role">{role}</div>
                    <div className="sign-line" />
                    <div className="sign-hint">Name &amp; Signature</div>
                    <div className="sign-line short" />
                    <div className="sign-hint">Date</div>
                  </div>
                ))}
              </div>

              <div className="foot-note">
                This voucher is a record of payment made from the accounts of {church?.name || 'the church'}.
                Retain with supporting receipts and invoices.
              </div>
            </div>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, bgcolor: '#FAFBFC', gap: 1, flexShrink: 0 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          A5 portrait · flows onto a second sheet only if the voucher runs long
        </Typography>
        <Tooltip title="Download as an Excel sheet">
          <Button onClick={handleExcel} startIcon={<GridOn />} variant="outlined">Excel</Button>
        </Tooltip>
        <Button onClick={handlePrint} startIcon={<Print />} variant="contained" sx={{ px: 3 }}>
          Print
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* Shared by the on-screen preview and the print window so what you see is what
   comes out of the printer. */
const SHEET_CSS = `
.sheet {
  width: 148mm; min-height: 210mm; padding: 10mm;
  background: #fff; color: #111;
  font-family: 'Helvetica Neue', -apple-system, Segoe UI, Roboto, Arial, sans-serif;
  font-size: 10px; line-height: 1.35;
  box-shadow: 0 8px 30px rgba(15,23,42,0.18);
}
.sheet .letterhead { display: flex; align-items: center; gap: 10px; padding-bottom: 8px; border-bottom: 2.5px double ${NAVY}; }
.sheet .letterhead img { width: 46px; height: 46px; object-fit: contain; border-radius: 4px; }
.sheet .logo-fallback { width: 46px; height: 46px; border-radius: 4px; background: ${NAVY}; color: ${GOLD};
  display: flex; align-items: center; justify-content: center; font-size: 22px; }
.sheet .letterhead .info h1 { margin: 0; font-size: 15px; letter-spacing: 0.01em; color: ${NAVY}; text-transform: uppercase; }
.sheet .letterhead .info p { margin: 2px 0 0; color: #666; font-size: 8.5px; }

.sheet .doc-title { text-align: center; margin: 10px 0 8px; }
.sheet .doc-title span { display: inline-block; padding: 3px 18px; font-size: 11px; font-weight: 800;
  letter-spacing: 0.2em; color: ${NAVY}; border-top: 1.2px solid ${NAVY}; border-bottom: 1.2px solid ${NAVY}; }

.sheet table { width: 100%; border-collapse: collapse; }
.sheet .mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.sheet .right { text-align: right; font-variant-numeric: tabular-nums; }

.sheet .meta { margin-bottom: 8px; border: 1px solid #D8DEE7; }
.sheet .meta th { text-align: left; width: 20%; padding: 4px 6px; font-size: 8px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.06em; color: #667; background: #F3F6FA;
  border: 1px solid #D8DEE7; white-space: nowrap; }
.sheet .meta td { padding: 4px 6px; font-size: 10px; font-weight: 600; border: 1px solid #D8DEE7; width: 30%; }

.sheet .items { margin-top: 4px; }
.sheet .items thead th { background: ${NAVY}; color: #fff; text-align: left; padding: 5px 6px;
  font-size: 8px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; }
.sheet .items thead th.right { text-align: right; }
.sheet .items .num { width: 22px; text-align: center; color: #889; }
.sheet .items tbody td { padding: 5px 6px; border-bottom: 1px solid #E6EAF0; vertical-align: top; }
.sheet .item-main { font-weight: 700; font-size: 10px; }
.sheet .item-sub { font-size: 8px; color: #667; text-transform: uppercase; letter-spacing: 0.04em; margin-top: 1px; }
.sheet .item-desc { font-size: 9px; color: #333; margin-top: 2px; font-style: italic; }
.sheet .item-pv { font-size: 8px; color: #99A; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; margin-top: 1px; }
.sheet .total-row td { border-top: 1.5px solid ${NAVY}; border-bottom: 2.5px double ${NAVY};
  padding: 6px; font-weight: 800; font-size: 11px; letter-spacing: 0.08em; color: ${NAVY}; background: #F8FAFC; }

.sheet .words { margin-top: 8px; padding: 6px 8px; border: 1px solid #D8DEE7; background: #FFFDF5; }
.sheet .words-label { display: block; font-size: 7.5px; font-weight: 700; text-transform: uppercase;
  letter-spacing: 0.1em; color: #889; }
.sheet .words-value { display: block; margin-top: 1px; font-size: 10px; font-weight: 700; color: ${NAVY}; }

.sheet .sign-block { display: flex; gap: 8px; margin-top: 18px; }
.sheet .sign { flex: 1; }
.sheet .sign-role { font-size: 8px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.1em; color: ${NAVY}; }
.sheet .sign-line { border-bottom: 1px solid #333; height: 22px; }
.sheet .sign-line.short { height: 16px; }
.sheet .sign-hint { font-size: 7px; color: #99A; margin-bottom: 4px; }

.sheet .foot-note { margin-top: 14px; padding-top: 6px; border-top: 1px solid #E6EAF0;
  font-size: 7.5px; color: #99A; text-align: center; }
`;
